import { ReactNode } from "react";

import { Box } from "@mui/material";
import { Apps } from "@mui/icons-material";
import { BsMetaUtil, LoanRowHealth, TokenListMetadata } from "@bridgesplit/abf-react";
import { ChipValue, Row, SkeletonRounded, Text, TextSkeleton, useAppPalette } from "@bridgesplit/ui";
import { formatUsd } from "@bridgesplit/utils";
import { RateDisplay } from "app/components/points/app/common";

import { TokenImage } from "../common";
import { GroupHealthCell, PositionCountTag } from "./common";

export function ChipHeader({
    icon,
    label,
    count
}: {
    icon: ReactNode;
    label: string | undefined;
    count: number | undefined;
}) {
    return (
        <Row spaceBetween sx={{ alignItems: "center", gap: 1 }}>
            <Row spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
                {icon}
                {label === undefined ? (
                    <TextSkeleton variant="body1" width="48px" />
                ) : (
                    <Text variant="body1">{label}</Text>
                )}
            </Row>
            {!!count && <PositionCountTag count={count} />}
        </Row>
    );
}

function ChipTokenIcon({ metadata }: { metadata: TokenListMetadata | undefined }) {
    return metadata ? <TokenImage size="sm" metadata={metadata} /> : <SkeletonRounded height={24} width="24px" />;
}

function AllChipIcon() {
    const { background } = useAppPalette();
    return (
        <Box
            sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: background,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            <Apps sx={{ fontSize: 14 }} />
        </Box>
    );
}

export function ChipStatPair({ left, right, loading }: { left?: ReactNode; right?: ReactNode; loading?: boolean }) {
    return (
        <Row spaceBetween sx={{ alignItems: "center", gap: 1 }}>
            {loading ? (
                <>
                    <TextSkeleton variant="body1" width="56px" />
                    <TextSkeleton variant="body1" width="44px" />
                </>
            ) : (
                <>
                    {left}
                    {right}
                </>
            )}
        </Row>
    );
}

export function HealthApyChipStats({
    healths,
    apy,
    loading
}: {
    healths: LoanRowHealth[];
    apy: number | undefined;
    loading?: boolean;
}) {
    return (
        <ChipStatPair
            loading={loading}
            left={<GroupHealthCell healths={healths} />}
            right={<RateDisplay apy={apy} variant="body1" color="body" isFeatured underline={false} />}
        />
    );
}

// Undefined `metadata` renders the loading skeleton.
export function PortfolioTokenChip({
    metadata,
    count,
    valueUsd,
    tokenAmount,
    stats
}: {
    metadata: TokenListMetadata | undefined;
    count: number | undefined;
    valueUsd: number | undefined;
    tokenAmount: number | undefined;
    stats: ReactNode;
}) {
    return (
        <>
            <ChipHeader
                icon={<ChipTokenIcon metadata={metadata} />}
                label={metadata ? BsMetaUtil.getSymbol(metadata) : undefined}
                count={count}
            />
            <ChipValue
                value={metadata ? formatUsd(valueUsd) : undefined}
                subtitle={metadata ? BsMetaUtil.formatRawAmount(metadata, tokenAmount) : undefined}
            />
            {stats}
        </>
    );
}

export function PortfolioAllChip({
    count,
    valueUsd,
    assetCount,
    stats,
    loading
}: {
    count: number | undefined;
    valueUsd: number | undefined;
    assetCount: number | undefined;
    stats: ReactNode;
    loading?: boolean;
}) {
    return (
        <>
            <ChipHeader icon={<AllChipIcon />} label="All" count={count} />
            <ChipValue
                value={loading ? undefined : formatUsd(valueUsd)}
                subtitle={assetCount !== undefined ? `across ${assetCount} assets` : undefined}
            />
            {stats}
        </>
    );
}
